
import { useEffect, useState, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_PERMISSIONS } from '@/types/permissions';
import { useAuthOperations } from './useAuthOperations';
import { usePermissionsLogic } from './usePermissionsLogic';
import type { UnifiedPermissionsContextValue } from './types';
import { UnifiedPermissionsContext } from './context';
import { SecureAuthTokenService } from '@/services/auth/SecureAuthTokenService';


// Helper function to validate session
const isValidSession = (session: Session | null): boolean => {
  if (!session) return false;
  
  // Check if session has required properties
  if (!session.user || !session.access_token) {
    return false;
  }
  
  // Check if session is expired
  const now = Math.floor(Date.now() / 1000);
  if (session.expires_at && session.expires_at < now) {
    return false;
  }
  
  return true;
};

export const UnifiedPermissionsProvider = ({ children }: { children: React.ReactNode }) => {
  // Debug logging for provider initialization

  // Auth state
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [initialAuthCheckComplete, setInitialAuthCheckComplete] = useState(false);
  
  // Refs for state management
  const toastShownRef = useRef({ signedIn: false, adminAccess: false });
  const signOutInProgressRef = useRef(false);
  const secureStorageInitialized = useRef(false);

  // Computed properties
  const isAuthenticated = !!session && !!user && isValidSession(session);

  // Debug logging for authentication state


  // Custom hooks
  const { signIn, signUp, updateUserMetadata, signOut } = useAuthOperations(toastShownRef, signOutInProgressRef);
  
  const {
    permissions,
    setPermissions,
    isRefreshing,
    error,
    subscriptionSource,
    upgradeInProgress,
    refreshPermissions,
    forceRefreshPermissions,
    initiateSubscription,
    debounceTimeoutRef,
    hasInitiallyRefreshedRef
  } = usePermissionsLogic(session, user, authReady);

  // Initialize secure storage
  useEffect(() => {
    const initializeSecureStorage = async () => {
      if (secureStorageInitialized.current) return;

      try {
        // Just initialize the service, don't attempt session restoration
        SecureAuthTokenService.getInstance();
        secureStorageInitialized.current = true;
      } catch (error) {
        console.error('🔐 Error initializing secure storage:', error);
        secureStorageInitialized.current = true; // Mark as initialized to prevent retries
      }
    };

    initializeSecureStorage();
  }, []);

  // Initialize auth state
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'USER_UPDATED' | 'PASSWORD_RECOVERY', newSession: Session | null) => {
        // Validate session before setting state
        if (newSession && !isValidSession(newSession)) {
          setSession(null);
          setUser(null);
        } else {
          setSession(newSession);
          setUser(newSession?.user ?? null);
        }
        
        // Mark that we've completed the initial auth check
        if (!initialAuthCheckComplete) {
          setInitialAuthCheckComplete(true);
        }
        
        // Reset toast flags on auth changes
        if (event === 'SIGNED_OUT') {
          toastShownRef.current.signedIn = false;
          toastShownRef.current.adminAccess = false;
          
          // Reset initial refresh tracking on sign out
          hasInitiallyRefreshedRef.current = false;
        }
      }
    );

    // Get initial session with error handling
    supabase.auth.getSession()
      .then(({ data: { session } }: { data: { session: Session | null } }) => {
        console.log('🔍 UnifiedPermissionsProvider: getSession() success:', {
          hasSession: !!session,
          userId: session?.user?.id
        });

        if (session && !isValidSession(session)) {
          console.log('❌ UnifiedPermissionsProvider: Session failed validation');
          setSession(null);
          setUser(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
        setInitialAuthCheckComplete(true);
      })
      .catch((error: unknown) => {
        console.error('❌ UnifiedPermissionsProvider: getSession() failed:', error);
        // Still mark auth check as complete even if getSession fails
        // This ensures the app doesn't get stuck waiting for auth
        setSession(null);
        setUser(null);
        setInitialAuthCheckComplete(true);
      });

    const timeout = debounceTimeoutRef.current;

    return () => {
      subscription.unsubscribe();
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAuthCheckComplete]); // Refs removed from dependency array to prevent infinite re-renders

  // Consolidated permission refresh based on auth state - triggers only once during initialization
  useEffect(() => {
    const now = Date.now();
    const userId = user?.id;

    // Don't proceed until we've completed the initial auth check
    if (!initialAuthCheckComplete) {
      return;
    }

    if (!userId) {
      setPermissions({
        ...DEFAULT_PERMISSIONS,
        permissionsStatus: 'ready',
        lastChecked: now
      });
      // Set authReady to true for unauthenticated users
      setAuthReady(true);
      return;
    }

    // Only refresh if we haven't done the initial refresh yet
    if (!hasInitiallyRefreshedRef.current) {
      refreshPermissions();
    }
  }, [initialAuthCheckComplete, user?.id, refreshPermissions, setPermissions, hasInitiallyRefreshedRef]);

  // Set authReady to true when authentication is ready, without waiting for permissions
  useEffect(() => {
    if (initialAuthCheckComplete) {
      setAuthReady(true);
    }
  }, [initialAuthCheckComplete, isAuthenticated]);

  // Check for cache expiration periodically during app usage
  useEffect(() => {
    if (!authReady || !user?.id || !permissions.isAuthenticated) return;

    // Check cache every 5 minutes during active usage
    const checkCacheInterval = setInterval(async () => {
      // Only check if user is authenticated and we have permissions
      if (permissions.isAuthenticated && user?.id) {
        // Import the cache checking function
        const { getCachedPermissions } = await import('@/services/permissions');
        const cachedPermissions = getCachedPermissions(user.id);

        if (!cachedPermissions) {
          console.log('🔄 Periodic cache check: Cache expired, triggering refresh');
          refreshPermissions();
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(checkCacheInterval);
  }, [authReady, user?.id, permissions.isAuthenticated, refreshPermissions]);

  // Log permissions state for debugging
  useEffect(() => {
    console.log('🔍 Permissions Provider: Current permissions state:', {
      permissions,
      isRefreshing,
      error,
      subscriptionSource,
      upgradeInProgress,
      userId: user?.id
    });
  }, [permissions, isRefreshing, error, subscriptionSource, upgradeInProgress, user?.id]);

  const contextValue: UnifiedPermissionsContextValue = {
    // Auth state
    user,
    session,
    authReady,
    
    // Permissions state
    ...permissions,
    isRefreshing,
    error,
    subscriptionSource,
    upgradeInProgress,
    
    // Methods
    refreshPermissions,
    forceRefreshPermissions,
    initiateSubscription,
    signIn,
    signUp,
    signOut,
    updateUserMetadata,
  };

  return (
    <UnifiedPermissionsContext.Provider value={contextValue}>
      {children}
    </UnifiedPermissionsContext.Provider>
  );
};
